import {
  ConditionAction,
  AutomationSchemaRef,
  CodeStepExecutionCacheMode,
  ConfigValues,
  DataField,
  DataInput,
  DatasetType,
  DynamicDataInput,
  ExecutionType,
  FileSelectionMode,
  FileCandidateScope,
  FilterMode,
  FilterType,
  InputSource,
  InsightField,
  IterationConfigMetadata,
  OutputAvailability,
  RuntimeOutputName,
  SchemaValidationMode,
  SpreadsheetFilterMode,
  StepType,
  TransformCachedCodeReuseScope,
  TransformCodeSourceMode,
  TransformReviewMode,
} from './automationFields';

import { StepErrorType } from '@/types/stepErrorTypes.ts';

export type StepError = {
  type: StepErrorType;
  message: string;
  index: number | null;
  secondary_index: number | null;
};

export type StepWarning = {
  type: 'provided_code_ignored_by_reuse_cache';
  message: string;
  index: number | null;
  secondary_index: number | null;
};

export type StepBase = {
  step_type: StepType;
  step_id: string;
  name: string;
  output_name: string;
  runtime_output_name?: RuntimeOutputName | null;
  description: string;
  errors: StepError[];
  warnings?: StepWarning[];
  is_disabled: boolean;
  wait_for_step_ids: string[];
  reasoning: StepReasoningEffort;
  primary_model_override: string | null;
  secondary_model_override: string | null;
  semantic_recovery_primary_model?: string | null;
  retry_config: StepLevelRetryConfig | null;
};

export type StepLevelRetryConfig = {
  enabled: boolean;
  max_retry_count: number;
  use_failure_feedback: boolean;
};

export enum StepReasoningEffort {
  Default = 'default',
  NoReasoning = 'none',
  Minimal = 'minimal',
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  XHigh = 'xhigh',
}

export enum ControlBlockingMode {
  None = 'none',
  SelectedSteps = 'selected_steps',
  AllLaterStepsInScope = 'all_later_steps_in_scope',
}

export enum TriggerType {
  Manual = 'manual',
  External = 'external',
  Scheduled = 'scheduled',
}

export type IntervalSchedule = {
  kind: 'interval';
  every_minutes: number;
  start_at: string;
};

export type IntegrationStepConfig = {
  integration_type: string | null;
  integration_id: string | null;
  action_type: string | null;
  config_values: ConfigValues;
};

export enum ManualTriggerInputType {
  File = 'file',
  Folder = 'folder',
  Text = 'text',
  Json = 'json',
}

export type ManualTriggerPortalFormControl =
  | 'text'
  | 'textarea'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'select'
  | 'date'
  | 'email'
  | 'url';

export type ManualTriggerPortalFormField = {
  path: string[];
  label: string;
  help_text?: string;
  placeholder?: string;
  control: ManualTriggerPortalFormControl;
  group?: string;
};

export type ManualTriggerPortalForm = {
  title?: string;
  description?: string;
  fields: ManualTriggerPortalFormField[];
};

export type ManualTriggerInput = {
  output_id: string;
  name: string;
  description: string;
  input_type: ManualTriggerInputType;
  provider_binding_id?: string | null;
  is_required: boolean;
  schema_ref?: { schema_id: string; version: number } | null;
  portal_form?: ManualTriggerPortalForm | null;
};

export type TriggerStep = StepBase & {
  step_type: StepType.Trigger;

  trigger_type: TriggerType;
  schedule?: IntervalSchedule | null;
  integration_config: IntegrationStepConfig;
  manual_trigger_inputs: ManualTriggerInput[];
};

export type DispatchStep = StepBase & {
  step_type: StepType.Dispatch;

  integration_config: IntegrationStepConfig;
};

export enum EntityModeType {
  DataInput = 'DataInput',
  Provided = 'Provided',
  Detect = 'Detect',
}

export type EntityDataInputConfig = {
  data_input: DataInput;
};
export type EntityProvidedConfig = {
  values: string[];
};
export enum EntityIdentifierRepresentationType {
  PlainString = 'plain_string',
  JsonObjectString = 'json_object_string',
}
export type EntityIdentifierContract = {
  representation: EntityIdentifierRepresentationType;
  required_object_keys: string[];
  allow_additional_object_keys: boolean;
};
export type EntityDetectConfig = {
  entity_description: string;
  identifier_contract: EntityIdentifierContract;
};
export type EntityConfig = EntityDataInputConfig | EntityProvidedConfig | EntityDetectConfig;

export enum FieldExtractModeType {
  Bulk = 'Bulk',
  Individual = 'Individual',
}

export enum EntityDetectionRoutingModeType {
  Infer = 'infer',
  DelegatedOnly = 'delegated_only',
  PreferDirect = 'prefer_direct',
}

export type ExtractDataStep = StepBase & {
  step_type: StepType.ExtractData;
  instructions_source: InputSource;
  instructions_data_inputs: DataInput[];
  instructions: string;
  include_citations: boolean;
  data_inputs: DataInput[];
  //////
  extract_entities: boolean;
  entity_mode: EntityModeType;
  entity_config: EntityConfig;
  supports_long_entity_extraction: boolean;
  entity_detection_routing_mode: EntityDetectionRoutingModeType;
  min_extract_count: number | 'Any';
  max_extract_count: number | 'Any';
  //////
  extract_fields_source: InputSource;
  extract_fields_data_input: DataInput;
  fields: DataField[];
  field_extract_mode: FieldExtractModeType;
  entity_field_batch_size: number;
  insights: InsightField[];
  insight_extract_mode: FieldExtractModeType;
  //////
  verify_extracted_data: boolean;
  verification_instructions: string;
  max_verification_repair_attempts: number;
};

export type TransformDataStep = StepBase & {
  step_type: StepType.TransformData;

  transformation_logic_source: InputSource;
  transformation_logic_data_inputs: DataInput[];
  transformation_logic_description: string;
  review_instructions: string;
  review_mode: TransformReviewMode;

  execution_type: ExecutionType;
  data_inputs: DataInput[];
  input_contracts?: TransformInputContract[];
  output_type: DatasetType;
  output_schema_ref?: AutomationSchemaRef | null;
  data_outputs?: TransformDataOutput[];
  code_source_mode: TransformCodeSourceMode;
  cached_code_reuse_scope: TransformCachedCodeReuseScope;
  step_execution_cache_mode?: CodeStepExecutionCacheMode;
  provided_code: string;
  intervention_response?: InterventionResponseContract | null;
  rerun_feedback_binding_name?: string;
};

export type TransformInputContract = {
  binding_name: string;
  accepted_dataset_types: DatasetType[];
  accepted_schema_refs: AutomationSchemaRef[];
  allowed_source_types: string[];
  required: boolean;
  allow_multiple: boolean;
  validation_mode: SchemaValidationMode;
};

export type InterventionResponseField = {
  key: string;
  label: string;
  description: string;
  field_type: 'text' | 'enum' | 'year_month';
  choices: string[];
};

export type InterventionResponseContract = {
  binding_name: string;
  fields: InterventionResponseField[];
};

export type DecisionControlLogic = {
  execution_type: ExecutionType;
  code_source_mode: TransformCodeSourceMode;
  cached_code_reuse_scope: TransformCachedCodeReuseScope;
  step_execution_cache_mode?: CodeStepExecutionCacheMode;
  provided_code: string;
  input_contracts?: TransformInputContract[];
};

export type TransformDataOutput = {
  output_id: string;
  name: string;
  output_type: DatasetType;
  schema_ref?: AutomationSchemaRef | null;
  runtime_output_name?: RuntimeOutputName | null;
};

export enum SubAutomationExecutionMode {
  Blocking = 'blocking',
  Async = 'async',
}

export const ASYNC_SUB_AUTOMATION_OUTPUT_ID = 'sub_automation_run_metadata';

export type SubAutomationOutputContractSnapshot = {
  dataset_type: DatasetType;
  availability?: OutputAvailability;
  schema_ref?: AutomationSchemaRef | null;
  schema_id: string | null;
  version: number | null;
  description: string;
  json_schema: Record<string, unknown> | null;
  validation_mode: SchemaValidationMode;
  artifact_kind?: 'immutable_file' | 'external_resource' | null;
  original_file_transport?: 'runtime_checked' | null;
  transport_contract_version?: 1 | 2;
  artifact_evidence_profile?: 'workbook' | 'blob_review' | 'external_resource' | null;
  expected_mime_types?: string[];
};

export type SubAutomationTriggerInputMapping = {
  trigger_input_id: string;
  data_input: DataInput;
};

export type SubAutomationPinnedOutput = {
  exposed_output_id: string;
  source_step_id: string;
  source_step_name: string;
  source_output_id: string | null;
  source_output_name: string | null;
  output_contract: SubAutomationOutputContractSnapshot;
};

export type PinnedSubAutomationStep = StepBase & {
  step_type: StepType.SubAutomation;
  binding_mode?: 'pinned';
  execution_mode: SubAutomationExecutionMode;
  target_automation_id: string | null;
  target_automation_version_id: string | null;
  target_trigger_step_id: string | null;
  /** Server-derived evidence for the selected immutable child interface. */
  target_interface_fingerprint?: string | null;
  trigger_input_mappings: SubAutomationTriggerInputMapping[];
  target_manual_trigger_inputs: ManualTriggerInput[];
  target_pinned_outputs: SubAutomationPinnedOutput[];
};

export type LogicalSubAutomationStep = StepBase & {
  step_type: StepType.SubAutomation;
  binding_mode: 'composition';
  target_automation_id: string;
  target_trigger_step_id: string;
  execution_mode: SubAutomationExecutionMode;
  expected_interface: import('./executionCompositions.ts').PublicInterface;
  trigger_input_mappings: SubAutomationTriggerInputMapping[];
};
export type SubAutomationStep = PinnedSubAutomationStep | LogicalSubAutomationStep;

/** Version-local module call. Its interface and bindings use dedicated composition operations. */
export type LocalSubflowCallStep = StepBase & {
  step_type: StepType.LocalSubflowCall;
  subflow_id: string;
  input_bindings: Array<{ port_id: string; data_input: DataInput }>;
  output_bindings: Array<{
    port_id: string;
    output_id: string;
    output_name: string;
    dataset_type: DatasetType;
    schema_ref?: AutomationSchemaRef | null;
  }>;
};

export type RunMetadataStep = StepBase & {
  step_type: StepType.RunMetadata;
};

export type IterateDataOutput = {
  output_id: string;
  name: string;
  child_data_output: DynamicDataInput;
  output_type: DatasetType;
};

export type IterateStep = StepBase & {
  step_type: StepType.Iterate;
  data_input: DataInput;
  item_name: string;
  metadata: IterationConfigMetadata;
  input_has_header_row: boolean;
  children: AutomationStep[];

  data_outputs: IterateDataOutput[];
};

export type RepeatDataInput = {
  output_id: string;
  name: string;
  data_input: DataInput;
  child_data_output: DynamicDataInput;
};

export type RepeatStep = StepBase & {
  step_type: StepType.Repeat;
  //
  condition_data_inputs: DataInput[];

  condition_logic_source: InputSource;
  condition_logic_data_inputs: DataInput[];
  condition_description: string;
  control_logic: DecisionControlLogic;
  min_repeat_count: number | 'Any';
  max_repeat_count: number | 'Any';
  repeat_data_inputs: RepeatDataInput[];
  //
  children: AutomationStep[];
};

export type RetryDataOutput = {
  output_id: string;
  name: string;
  child_data_output: DynamicDataInput;
};

export enum RetryStepMode {
  Disabled = 'disabled',
  RetryOnChildError = 'retry_on_child_error',
  RetryOnClassificationSignal = 'retry_on_classification_signal',
}

export enum FreshContextRetrySchedule {
  Immediate = 'immediate',
  PlatformBackoff = 'platform_backoff',
}

export type RetryStepConfig = {
  mode: RetryStepMode;
  classification_step_id: string | null;
  fresh_context_retry_schedule: FreshContextRetrySchedule;
  stop_on_ambiguous_outcome: boolean;
  retry_budget_exhausted_action: string;
  retry_budget_exhausted_policies: unknown[];
};

export type RetryStep = StepBase & {
  step_type: StepType.Retry;
  max_retry_count: number;
  retry_step_config: RetryStepConfig | null;
  data_outputs: RetryDataOutput[];
  children: AutomationStep[];
};

export type ErrorBoundaryEventOutput = {
  output_id: string;
  name: string;
};

export type ErrorBoundaryOutputAlias = {
  output_id: string;
  name: string;
  child_data_output: DynamicDataInput;
};

export type ErrorBoundaryRecoveryCheckpoint = {
  checkpoint_id: string;
  name: string;
  source_step_id: string;
};

export type ErrorBoundaryInputBinding = {
  output_id: string;
  name: string;
  data_input: DynamicDataInput;
};

export type ErrorBoundaryRecoveryManifestOutput = {
  output_id: string;
  name: string;
};

export type ErrorBoundaryRecoveryReceiptOutput = {
  output_id: string;
  name: string;
};

export enum ErrorBoundaryRecoveryPolicy {
  RethrowOriginalFailure = 'rethrow_original_failure',
  ContinueAfterRecovery = 'continue_after_recovery',
}

export type FailureHandlerStep = StepBase & {
  step_type: StepType.FailureHandler;
  failure_event_output: ErrorBoundaryEventOutput;
  recovery_policy: ErrorBoundaryRecoveryPolicy;
  protected_input_bindings: ErrorBoundaryInputBinding[];
  handler_input_bindings: ErrorBoundaryInputBinding[];
  protected_success_outputs: ErrorBoundaryOutputAlias[];
  recovery_outputs: ErrorBoundaryOutputAlias[];
  recovery_checkpoints: ErrorBoundaryRecoveryCheckpoint[];
  recovery_manifest_output: ErrorBoundaryRecoveryManifestOutput | null;
  recovery_receipt_output: ErrorBoundaryRecoveryReceiptOutput | null;
  recovery_continuation_outputs: ErrorBoundaryOutputAlias[];
  children: AutomationStep[];
  handler_children: AutomationStep[];
};

export type ConditionalDataOutput = {
  output_id: string;
  name: string;
  schema_ref?: AutomationSchemaRef | null;
  may_be_unavailable?: boolean;
};

export type ConditionDataOutput = {
  output_id: string;
  data_input: DataInput;
  child_data_output: DynamicDataInput;
  literal_json?: Record<string, unknown> | null;
  schema_ref?: AutomationSchemaRef | null;
};

export type Condition = {
  condition_id: string;
  name: string;
  is_disabled?: boolean;
  disabled_reason?: string;
  condition_data_inputs: DataInput[];

  condition_logic_source: InputSource;
  condition_logic_data_inputs: DataInput[];
  condition_logic_description: string;
  control_logic: DecisionControlLogic;

  action: ConditionAction;

  children: AutomationStep[];

  data_outputs: ConditionDataOutput[];
};

export type ConditionalStep = StepBase & {
  step_type: StepType.Conditional;

  data_outputs: ConditionalDataOutput[];

  conditions: Condition[];
};

export type CreateDirectoryStep = StepBase & {
  step_type: StepType.CreateDirectory;
  data_inputs: DataInput[];
};

export type GetFileFromDirectoryStep = StepBase & {
  step_type: StepType.GetFileFromDirectory;
  file_to_get_source: InputSource;
  file_to_get_data_input: DataInput;
  file_to_get_description: string;
  candidate_scope?: FileCandidateScope;
  selection_mode: FileSelectionMode;
  selection_value_source: InputSource;
  selection_value: string;
  selection_value_data_input: DataInput;
  data_input: DataInput;
  selected_output_contract?: {
    dataset_type: DatasetType;
    schema_ref: AutomationSchemaRef | null;
  } | null;
};

export type FilterStep = StepBase & {
  step_type: StepType.Filter;
  filter_condition_source: InputSource;
  filter_condition_data_inputs: DataInput[];
  dataset_description: string;
  is_relevant_condition: string;
  is_not_relevant_condition: string;
  filter_mode: FilterMode;
  filter_type: FilterType;
  min_selected_count: number | null;
  max_selected_count: number | null;
  spreadsheet_filter_mode: SpreadsheetFilterMode;
  selection_source: InputSource;
  selection_data_inputs: DataInput[];
  selection_instructions: string;
  data_input: DataInput;
};

export type ValidateStep = StepBase & {
  step_type: StepType.Validate;
  validation_logic_source: InputSource;
  validation_logic_data_inputs: DataInput[];
  validation_instructions: string;
  validation_succeed_logic_description: string;
  validation_fail_logic_description: string;
  control_logic: DecisionControlLogic;
  data_inputs: DataInput[];
  blocking_mode: ControlBlockingMode;
  blocking_step_ids: string[];
};

export type HumanDecisionResponseFieldType = 'enum' | 'text' | 'boolean' | 'number' | 'date';

export type HumanDecisionResponseField = {
  field_id: string;
  label: string;
  field_type: HumanDecisionResponseFieldType;
  required: boolean;
  enum_values: string[];
  max_length: number | null;
};

export type HumanDecisionGateStep = StepBase & {
  step_type: StepType.HumanDecisionGate;
  data_inputs: DataInput[];
  response_schema_version: string;
  response_fields: HumanDecisionResponseField[];
  require_reason: boolean;
  reviewer_policy: 'project_update';
  expiry_mode: 'expire' | 'resume_without_refresh' | 'refresh_read_only_dependencies' | 'refresh_then_require_reapproval';
  expiry_hours: number | null;
  refresh_step_ids: string[];
};

export type AutomationStep =
  | TriggerStep
  | DispatchStep
  | ExtractDataStep
  | TransformDataStep
  | SubAutomationStep
  | LocalSubflowCallStep
  | RunMetadataStep
  | IterateStep
  | RepeatStep
  | RetryStep
  | FailureHandlerStep
  | ConditionalStep
  | CreateDirectoryStep
  | GetFileFromDirectoryStep
  | FilterStep
  | ValidateStep
  | HumanDecisionGateStep;

export type ParentStep = {
  step: AutomationStep;
  index: number; // zero-based index within its parent
};

export type AutomationStepWithChildren =
  | IterateStep
  | RepeatStep
  | RetryStep
  | FailureHandlerStep
  | ConditionalStep
  | LocalSubflowCallStepWithChildren;

export type LocalSubflowCallStepWithChildren = LocalSubflowCallStep & {
  children: AutomationStep[];
};

export type AutomationStepWithDirectChildren =
  | IterateStep
  | RepeatStep
  | RetryStep
  | LocalSubflowCallStepWithChildren;

export type AutomationStepMap = Record<string, AutomationStep>;
